import React from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import { CmsEntities } from 'client/data/models/cms';
import { parseLinkMetadata } from 'site-modules/shared/utils/parse-content-metadata';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { RelatedContentItem } from './related-content-item';

import './related-content.scss';

export function RelatedContent({
  entry,
  titleClassName = 'heading-3 mb-0',
  config: { PHOTO_SIZE, COLUMN_NUMBER, MAX_CONTENT_LENGTH, ITEM_CLASS_NAME, ASPECT_RATIO },
  isMobile = false,
  titleComponent = 'h3',
}) {
  const relatedContent = entry.links().map(({ href, rel }) => ({
    photoUrl: href,
    ...parseLinkMetadata(entry, rel),
  }));

  if (!relatedContent.length) {
    return null;
  }

  const relatedContentTitle = entry.metadata('card-title').value();
  const relatedContentSubtitle = entry.metadata('card-subtitle').value();
  const maxContentLength = isMobile ? MAX_CONTENT_LENGTH.MOBILE : MAX_CONTENT_LENGTH.DESKTOP;
  const hasCollapsedRelatedContent = relatedContent.length > maxContentLength;
  const configProps = {
    className: ITEM_CLASS_NAME,
    columnNumber: COLUMN_NUMBER,
    photoSize: PHOTO_SIZE,
    aspectRatio: ASPECT_RATIO,
  };

  const Title = titleComponent;

  return (
    <div className="related-content text-gray-darker mt-1_5">
      {!!relatedContentTitle && <Title className={titleClassName}>{relatedContentTitle}</Title>}
      {!!relatedContentSubtitle && <div className="mt-0_25">{relatedContentSubtitle}</div>}
      <Row className="mt-0_5 p-0 mb-0" tag="ol" style={{ listStyle: 'none' }}>
        {relatedContent.slice(0, maxContentLength).map((relatedContentItem, index) => {
          const key = `related-content-${relatedContentItem.url}-${index}`;
          return <RelatedContentItem key={key} {...relatedContentItem} {...configProps} componentToUse="li" />;
        })}
      </Row>
      {hasCollapsedRelatedContent && (
        <ContentCollapse
          textClosed="See more"
          textOpen="See less"
          justify="justify-content-start"
          btnClasses="me-0_25 p-0"
          btnContainerClasses="px-0_5 mt-1"
          btnTextClasses="fw-normal size-16 text-primary-darker"
          btnArrowClasses="size-10 text-primary-darker"
          showBtnBelow
          isEclipseFade={false}
        >
          <Row className="p-0" tag="ul" style={{ listStyle: 'none' }}>
            {relatedContent.slice(maxContentLength).map(relatedContentItem => (
              <RelatedContentItem
                key={`related-content-${relatedContentItem.url}`}
                {...relatedContentItem}
                {...configProps}
                componentToUse="li"
              />
            ))}
          </Row>
        </ContentCollapse>
      )}
    </div>
  );
}

RelatedContent.propTypes = {
  entry: CmsEntities.Content.isRequired,
  config: PropTypes.shape({
    ITEM_CLASS_NAME: PropTypes.string,
    PHOTO_SIZE: PropTypes.number,
    COLUMN_NUMBER: PropTypes.shape({
      MOBILE: PropTypes.number,
      DESKTOP: PropTypes.number,
    }),
    MAX_CONTENT_LENGTH: PropTypes.shape({
      MOBILE: PropTypes.number,
      DESKTOP: PropTypes.number,
    }),
  }).isRequired,
  titleClassName: PropTypes.string,
  isMobile: PropTypes.bool,
  titleComponent: PropTypes.elementType,
};
